import React, { useEffect, useMemo } from 'react'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { Draft, WorkspaceData, useWorkspace } from 'zens-store'
import { Spacing, Text } from 'zens-storybook'
import { Form, NavigationConfirm, FloorField, WorkspaceType, FormProps } from '@dms/component'

const styles = StyleSheet.create({
  location: {
    minHeight: 78,
  },
  workspace: {
    zIndex: 200,
  },
  skeletonFloor: {
    marginTop: Spacing.XS,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  workspaceId: number
  onSave?: (draft: Draft<WorkspaceData>) => Promise<void>
  onDelete?: (draft: Draft<WorkspaceData>) => Promise<void>
}
const WorkspaceEditForm = (props: Props) => {
  const { companyId, workspaceId, onSave, onDelete, ...otherProps } = props
  const { t } = useTranslation()
  const workspace = useWorkspace({ companyId, workspaceId })

  const draft = useMemo(() => new Draft({ id: 0, ...workspace.data?.data }), [workspace.data])

  useEffect(() => {
    workspace.data?.data &&
      Object.keys(workspace.data?.data).forEach((key) =>
        draft.update(key as keyof WorkspaceData, workspace.data?.data[key as keyof WorkspaceData]),
      )
  }, [workspace.data?.data])

  const config = {
    groups: [
      {
        fields: [
          {
            key: 'floor_id',
            label: t('Located at'),
            FieldInputComponent: FloorField,
            fieldProps: { companyId },
            style: styles.location,
          },
          {
            key: 'name',
            label: t('Workspace name'),
          },
          {
            key: 'workspace_type_id',
            label: t('Workspace type'),
            FieldInputComponent: WorkspaceType,
            fieldProps: { companyId },
          },
        ],
        style: styles.workspace,
      },
      {
        label: t('Product information'),
        fields: [
          {
            key: 'vendor',
            label: t('Vendor'),
            optional: true,
          },
          {
            key: 'article_number',
            label: t('Article number'),
            optional: true,
          },
        ],
      },
    ],
  }
  const schema = object({
    name: string().required(t('Workspace name is required')),
    workspace_type_id: string().required(t('Workspace type is required')),
  })
  return (
    <>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Save'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              href: `/companies/${companyId}/workspaces/${workspaceId}`,
              light: true,
            },
          ],
          [
            {
              title: t('Delete workspace'),
              onPress: onDelete,
              left: 'trash' as const,
              danger: true,
            },
          ],
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        fields: [
          {
            key: 'floor_id',
            label: t('Located at'),
            FieldInputComponent: Text.Skeleton,
            inputStyle: styles.skeletonFloor,
            style: styles.location,
          },
          {
            key: 'name',
            label: t('Workspace name'),
          },
          {
            key: 'workspace_type_id',
            label: t('Workspace type'),
          },
        ],
        style: styles.workspace,
      },
      {
        label: t('Product information'),
        fields: [
          {
            key: 'vendor',
            label: t('Vendor'),
          },
          {
            key: 'article_number',
            label: t('Article number'),
          },
        ],
      },
    ],
  }

  return <Form.Skeleton config={config} numberOfbuttons={[2, 1]} {...props} />
}
WorkspaceEditForm.Skeleton = Skeleton

export default WorkspaceEditForm
